import React, { useState } from 'react';
import PropTypes from 'prop-types';
import turf from '@turf/turf';
import { isEmpty, map } from 'lodash';
import { Form, Button, Row, Col } from 'react-bootstrap';

import { LAYER_SUBTYPES, GEOJSON_GEOMETRIES } from '../../../api/Layers/Layers';
import { getMapLocation } from '../../../api/Layers/layer-operations';

import { coordinatesToXYZ } from '../../../modules/coordinates-to-xyz';

import ModalMap from '../ModalMap';
import handleErrorInClient from '../../../modules/handle-error-in-client';

const LayerMapBoxFields = ({
  deployEnv,
  isExternal,
  layer,
  layerGeometry,
  layerSubType,
  mapboxLayerName,
  mapboxLayerNames,
  changeLayerEditorStateProperty,
  loadMapBoxFromLayerName,
  loadMapBoxLayersOrGeoJSON
}) => {
  if (layerSubType !== LAYER_SUBTYPES.MAPBOX) return '';

  if (!isEmpty(mapboxLayerName) && !isExternal)
    return (
      <Form.Group>
        <Form.Label>Layer Name</Form.Label>
        <Form.Control type="text" name="mapboxLayerNameSelect" value={mapboxLayerName} readOnly />
      </Form.Group>
    );

  if (!isExternal) return '';

  const [defaultMapCenter, changeDefaultCenter] = useState(
    isEmpty(getMapLocation(layer)) ? turf.point([0, 0], { zoom: 0 }) : getMapLocation(layer)
  );
  const [selectedLayer, changeSelectedLayer] = useState(mapboxLayerName);
  const [showModal, handleShowModal] = useState(false);

  const selectLayer = event => {
    changeSelectedLayer(event.target.value.trim());
    loadMapBoxFromLayerName(event);
  };

  const data = coordinatesToXYZ(defaultMapCenter);

  return (
    <>
      <Form.Group>
        <Form.Label>Layer Name</Form.Label>
        <Form.Control
          as="select"
          name="mapboxLayerNameSelect"
          value={mapboxLayerName}
          onChange={selectLayer}
        >
          <option value="">Other Layer Name</option>;
          {map(mapboxLayerNames, layerName => {
            return (
              <option value={layerName} key={layerName}>
                {layerName}
              </option>
            );
          })}
        </Form.Control>
        {isEmpty(selectedLayer) && (
          <Form.Control
            name="mapboxLayerNameText"
            placeholder="Write a different Layer Name"
            defaultValue=""
          />
        )}
      </Form.Group>
      <Form.Group>
        <Form.Label>Layer Geometry</Form.Label>
        <Form.Control
          as="select"
          name="mapboxGeometry"
          value={layerGeometry}
          onChange={event =>
            changeLayerEditorStateProperty('layerGeometry', event.target.value.trim())
          }
        >
          {map(GEOJSON_GEOMETRIES, geometry => {
            return (
              <option value={geometry} key={geometry}>
                {geometry}
              </option>
            );
          })}
        </Form.Control>
      </Form.Group>
      <Form.Group as={Row}>
        {isEmpty(mapboxLayerNames) && (
          <Col
            as={Button}
            xs={{ span: 10, offset: 1 }}
            variant="primary"
            onClick={() => handleShowModal(true)}
            className="mb-2 mr-5"
          >
            Select A Fetch Point
          </Col>
        )}
        <Form.Label column xs={6} sm={1}>
          X
        </Form.Label>
        <Col xs={6} sm={3}>
          <Form.Control type="number" name="mapboxLayerX" value={data.x} readOnly />
        </Col>
        <Form.Label column xs={6} sm={1}>
          Y
        </Form.Label>
        <Col xs={6} sm={3}>
          <Form.Control type="number" name="mapboxLayerY" value={data.y} readOnly />
        </Col>
        <Form.Label column xs={6} sm={1}>
          Z
        </Form.Label>
        <Col xs={6} sm={3}>
          <Form.Control type="number" name="mapboxLayerZ" value={data.z} readOnly />
        </Col>
        <ModalMap
          defaultMapCenter={defaultMapCenter}
          deployEnv={deployEnv}
          headerText="Select Point To Fetch"
          isShownMap={showModal}
          hideMap={async () => {
            try {
              const event = new Event('input', { bubbles: true });
              const input = document.getElementsByName('mapboxLayerX')[0];
              input.dispatchEvent(event);
              await loadMapBoxLayersOrGeoJSON(event);
              handleShowModal(false);
            } catch (error) {
              handleErrorInClient('Please select new point');
            }
          }}
          updateDefaultCenter={changeDefaultCenter}
        />
      </Form.Group>
    </>
  );
};

LayerMapBoxFields.propTypes = {
  deployEnv: PropTypes.string.isRequired,
  isExternal: PropTypes.bool.isRequired,
  layer: PropTypes.object.isRequired,
  layerGeometry: PropTypes.string.isRequired,
  layerSubType: PropTypes.string.isRequired,
  mapboxLayerName: PropTypes.string.isRequired,
  mapboxLayerNames: PropTypes.array.isRequired,
  changeLayerEditorStateProperty: PropTypes.func.isRequired,
  loadMapBoxFromLayerName: PropTypes.func.isRequired,
  loadMapBoxLayersOrGeoJSON: PropTypes.func.isRequired
};

export default LayerMapBoxFields;
